/**
 * Workspace Format Conversion Utilities
 *
 * Converts between dict-based workspace state (from Report Library)
 * and array-based formats (for React Flow rendering).
 *
 * Key conversions:
 * - Dict nodes → LogicalNode array (for React Flow)
 * - Adjacency list edges → LogicalEdge array (for React Flow)
 * - Format detection for handling multiple data formats
 */

import {
  DictWorkspaceState,
  DictNode,
  DictFinding,
  WorkspaceFormat,
  EMPTY_DICT_WORKSPACE,
} from '../types/workspaceState';
// Import from workspace types for consistent type definitions
import {
  LogicalNode,
  LogicalEdge,
  LogicalChainGraph,
  NodeType,
} from '../components/workspace/types';
// Import Finding from apiService (not duplicated in types.ts)
import { Finding } from '../apiService';

// =============================================================================
// FORMAT DETECTION
// =============================================================================

/**
 * Check if workspace data is in dict format (from /workspaces/{id}/state)
 */
export function isDictWorkspaceFormat(data: unknown): data is DictWorkspaceState {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;

  // Dict format has nodes as object (not array) with node objects as values
  if (typeof d.nodes !== 'object' || Array.isArray(d.nodes)) return false;
  if (typeof d.edges !== 'object' || Array.isArray(d.edges)) return false;

  // Additional check: dict format has workspace_id and sequence_number
  return typeof d.workspace_id === 'string' && typeof d.sequence_number === 'number';
}

/**
 * Detect workspace format from data structure
 */
export function detectWorkspaceFormat(data: unknown): WorkspaceFormat {
  if (!data || typeof data !== 'object') return 'empty';

  const d = data as Record<string, unknown>;

  // Check for dict format first (from /workspaces/{id}/state)
  if (isDictWorkspaceFormat(data)) return 'dict';

  // Check for process graph format (flat with parent_id)
  if (Array.isArray(d.nodes) && d.nodes.length > 0) {
    const firstNode = d.nodes[0] as Record<string, unknown>;
    if ('parent_id' in firstNode) return 'process';
  }

  // Default to legacy array format
  if (Array.isArray(d.nodes) && Array.isArray(d.edges)) return 'legacy';

  return 'empty';
}

// =============================================================================
// DICT → ARRAY CONVERSIONS (for React Flow rendering)
// =============================================================================

/**
 * Convert dict nodes to LogicalNode array
 */
export function dictNodesToArray(nodes: Record<string, DictNode>): LogicalNode[] {
  return Object.values(nodes).map((node) => ({
    node_id: node.node_id,
    node_type: node.node_type as NodeType,
    cluster_id: node.cluster_id,
    // Backend uses 'name' internally with serialization_alias='label'.
    // Depending on serialization mode, either field may be populated.
    label: node.label || (node as any).name || '',
    relevance_note: node.relevance_note || '',
    content: node.content,
    finding_ids: node.finding_ids || [],
    detail: null,
    reference_ids: [],
    source_metadata: (node.metadata as any) || null,
    created_at: node.created_at,
    updated_at: node.updated_at,
    // Plan graph fields — pass through for EvidenceBoard rendering
    workspace_type: node.workspace_type,
    workspace_id: node.workspace_id,
    deliverable: node.deliverable,
    validation_status: node.validation_status as LogicalNode['validation_status'],
    reviews: node.reviews as LogicalNode['reviews'],
  }));
}

/**
 * Convert adjacency list edges to LogicalEdge array.
 * When edge_metadata is provided, enriches edges with label and edge_type.
 */
export function adjacencyListToEdges(
  edges: Record<string, string[]>,
  edgeMetadata?: Record<string, Record<string, { label?: string; edge_type?: string; output_spec?: string }>>
): LogicalEdge[] {
  const result: LogicalEdge[] = [];

  for (const [sourceId, targetIds] of Object.entries(edges)) {
    for (const targetId of targetIds) {
      const meta = edgeMetadata?.[sourceId]?.[targetId];
      const edge: any = {
        source_node_id: sourceId,
        target_node_id: targetId,
      };
      // Attach label from edge_metadata (output_spec takes priority as visible label)
      if (meta?.output_spec) {
        edge.label = meta.output_spec;
      } else if (meta?.label) {
        edge.label = meta.label;
      }
      if (meta?.edge_type) {
        edge.edge_type = meta.edge_type;
      }
      result.push(edge);
    }
  }

  return result;
}

/**
 * Convert dict findings to Finding record
 */
export function dictFindingToRecordFinding(finding: DictFinding): Finding {
  return {
    ...finding,
    finding_id: finding.finding_id,
    source_document_celex: finding.source_document_celex || '',
    source_document_name: finding.source_document_name || '',
    source_reference: finding.source_reference || '',
    source_reference_hierarchy: finding.source_reference_hierarchy || '',
    source_text: finding.source_text || '',
    source_snippet: finding.source_snippet || '',
    extracted_at: finding.extracted_at || '',
  };
}

export function dictFindingsToRecord(
  findings: Record<string, DictFinding>
): Record<string, Finding> {
  const result: Record<string, Finding> = {};

  for (const [id, finding] of Object.entries(findings)) {
    result[id] = dictFindingToRecordFinding(finding);
  }

  return result;
}

/**
 * Convert full dict workspace to LogicalChainGraph for React Flow rendering.
 * Passes edge_metadata through so edges carry output_spec labels.
 */
export function dictWorkspaceToGraph(state: DictWorkspaceState): LogicalChainGraph {
  return {
    nodes: dictNodesToArray(state.nodes),
    edges: adjacencyListToEdges(state.edges, state.edge_metadata),
  };
}

// =============================================================================
// ARRAY → DICT CONVERSIONS (for workspace operations)
// =============================================================================

/**
 * Convert LogicalNode array to dict format
 */
export function arrayNodesToDict(nodes: LogicalNode[]): Record<string, DictNode> {
  const result: Record<string, DictNode> = {};

  for (const node of nodes) {
    result[node.node_id] = {
      node_id: node.node_id,
      node_type: node.node_type,
      cluster_id: node.cluster_id ?? null,
      label: node.label,
      content: node.content,
      relevance_note: node.relevance_note || '',
      parent_ids: [], // Computed from edges
      finding_ids: node.finding_ids || [],
      materials: [],
      options: [],
      created_at: node.created_at,
      updated_at: node.updated_at,
    };
  }

  return result;
}

/**
 * Convert LogicalEdge array to adjacency list and compute parent_ids
 */
export function edgesToAdjacencyList(
  edges: LogicalEdge[],
  nodes: Record<string, DictNode>
): Record<string, string[]> {
  const result: Record<string, string[]> = {};

  // Initialize empty arrays for all nodes
  for (const nodeId of Object.keys(nodes)) {
    result[nodeId] = [];
  }

  // Build adjacency list and update parent_ids
  for (const edge of edges) {
    if (!result[edge.source_node_id]) {
      result[edge.source_node_id] = [];
    }

    if (!result[edge.source_node_id].includes(edge.target_node_id)) {
      result[edge.source_node_id].push(edge.target_node_id);
    }

    // Update parent_ids on target node
    if (nodes[edge.target_node_id]) {
      if (!nodes[edge.target_node_id].parent_ids.includes(edge.source_node_id)) {
        nodes[edge.target_node_id].parent_ids.push(edge.source_node_id);
      }
    }
  }

  return result;
}

/**
 * Convert Finding record to DictFinding record
 */
export function recordFindingToDictFinding(finding: Finding): DictFinding {
  return {
    ...finding,
    finding_id: finding.finding_id,
    source_document_celex: finding.source_document_celex,
    source_document_name: finding.source_document_name,
    source_reference: finding.source_reference,
    source_reference_hierarchy: finding.source_reference_hierarchy,
    source_text: finding.source_text,
    source_snippet: finding.source_snippet,
    extracted_at: finding.extracted_at,
  };
}

export function recordFindingsToDict(
  findings: Record<string, Finding>
): Record<string, DictFinding> {
  const result: Record<string, DictFinding> = {};

  for (const [id, finding] of Object.entries(findings)) {
    result[id] = recordFindingToDictFinding(finding);
  }

  return result;
}

/**
 * Convert LogicalChainGraph to dict workspace format
 */
export function graphToDictWorkspace(
  graph: LogicalChainGraph,
  workspaceId: string,
  existingFindings?: Record<string, Finding>
): DictWorkspaceState {
  const nodes = arrayNodesToDict(graph.nodes);
  const edges = edgesToAdjacencyList(graph.edges, nodes);

  return {
    ...EMPTY_DICT_WORKSPACE,
    workspace_id: workspaceId,
    nodes,
    edges,
    findings: existingFindings ? recordFindingsToDict(existingFindings) : {},
    derived_at: new Date().toISOString(),
  };
}

// =============================================================================
// WORKSPACE OPERATIONS HELPERS (for dict-based mutations)
// =============================================================================

/**
 * Add or update a node in dict workspace (natural deduplication)
 */
export function upsertNode(
  nodes: Record<string, DictNode>,
  node: Partial<DictNode> & { node_id: string }
): Record<string, DictNode> {
  const existing = nodes[node.node_id];

  return {
    ...nodes,
    [node.node_id]: {
      node_id: node.node_id,
      node_type: node.node_type || existing?.node_type || 'concept',
      cluster_id: node.cluster_id ?? existing?.cluster_id ?? null,
      label: node.label || existing?.label || '',
      content: node.content ?? existing?.content,
      relevance_note: node.relevance_note ?? existing?.relevance_note ?? '',
      parent_ids: node.parent_ids ?? existing?.parent_ids ?? [],
      finding_ids: node.finding_ids ?? existing?.finding_ids ?? [],
      materials: node.materials ?? existing?.materials ?? [],
      options: node.options ?? existing?.options ?? [],
      created_at: existing?.created_at || node.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
  };
}

/**
 * Remove a node and its associated edges
 */
export function removeNode(
  nodes: Record<string, DictNode>,
  edges: Record<string, string[]>,
  nodeId: string
): { nodes: Record<string, DictNode>; edges: Record<string, string[]> } {
  // Remove the node
  const { [nodeId]: _, ...remainingNodes } = nodes;

  // Remove edges from this node
  const { [nodeId]: __, ...remainingEdges } = edges;

  // Remove references to this node from other edges
  const cleanedEdges: Record<string, string[]> = {};
  for (const [sourceId, targetIds] of Object.entries(remainingEdges)) {
    cleanedEdges[sourceId] = targetIds.filter((id) => id !== nodeId);
  }

  // Remove from parent_ids of other nodes
  const cleanedNodes: Record<string, DictNode> = {};
  for (const [id, node] of Object.entries(remainingNodes)) {
    cleanedNodes[id] = {
      ...node,
      parent_ids: node.parent_ids.filter((pid) => pid !== nodeId),
    };
  }

  return { nodes: cleanedNodes, edges: cleanedEdges };
}

/**
 * Add an edge (connection) between nodes
 */
export function addEdge(
  nodes: Record<string, DictNode>,
  edges: Record<string, string[]>,
  sourceId: string,
  targetId: string
): { nodes: Record<string, DictNode>; edges: Record<string, string[]> } {
  // Add to adjacency list
  const newEdges = { ...edges };
  if (!newEdges[sourceId]) {
    newEdges[sourceId] = [];
  }
  if (!newEdges[sourceId].includes(targetId)) {
    newEdges[sourceId] = [...newEdges[sourceId], targetId];
  }

  // Update parent_ids on target node
  const newNodes = { ...nodes };
  if (newNodes[targetId] && !newNodes[targetId].parent_ids.includes(sourceId)) {
    newNodes[targetId] = {
      ...newNodes[targetId],
      parent_ids: [...newNodes[targetId].parent_ids, sourceId],
    };
  }

  return { nodes: newNodes, edges: newEdges };
}

/**
 * Remove an edge between nodes
 */
export function removeEdge(
  nodes: Record<string, DictNode>,
  edges: Record<string, string[]>,
  sourceId: string,
  targetId: string
): { nodes: Record<string, DictNode>; edges: Record<string, string[]> } {
  // Remove from adjacency list
  const newEdges = { ...edges };
  if (newEdges[sourceId]) {
    newEdges[sourceId] = newEdges[sourceId].filter((id) => id !== targetId);
  }

  // Update parent_ids on target node
  const newNodes = { ...nodes };
  if (newNodes[targetId]) {
    newNodes[targetId] = {
      ...newNodes[targetId],
      parent_ids: newNodes[targetId].parent_ids.filter((id) => id !== sourceId),
    };
  }

  return { nodes: newNodes, edges: newEdges };
}
